import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./boot/legacyShim";
import "./boot/rewardfulGuard";
import "./index.css";
import "./polyfills/react-polyfill";

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

const root = createRoot(rootElement);
const path = window.location.pathname.toLowerCase();

// Marketing pages that don't need the full product app bundle
const MARKETING_PATHS = new Set([
  "/blog", "/playbook", "/predictable", "/positioning", "/compounding", "/compounding-model", "/operators", "/acquire",
  "/dm", "/dm-course", "/dmcourse", "/conversion", "/fox",
  "/followers", "/followers-training", "/course", "/courses",
  "/profit", "/about", "/thank-you", "/thankyou", "/apply",
  "/privacy-policy", "/privacypolicy", "/terms-of-service", "/termsofservice",
  "/unsubscribed", "/careers", "/public-feedback", "/demo", "/contact",
]);

const isHomePage =
  path === "/" ||
  path === "/index.html" ||
  path === "" ||
  path === "/home";

// Blog post pages match /blog/:slug
const isBlogPost = path.startsWith("/blog/") && path !== "/blog";
const isMarketingPage = MARKETING_PATHS.has(path) || isBlogPost;

// ── Blog data prefetch ────────────────────────────────────────────────────────
// Fire Supabase RPC calls immediately (before chunk downloads complete) so that
// by the time Blog.tsx mounts and its useEffects run, the data is already ready.
// Blog.tsx reads window.__adtaria_blog_prefetch and consumes each promise once.
if (path === "/blog") {
  const SUPA_URL = "https://afwygrijuoqnjismnbgd.supabase.co";
  const ANON    = "sb_publishable_vNDpsP1wXRjMr6A8RtWT7Q_R4ynZ8GA";
  const rpc = (name: string, body: Record<string, unknown>): Promise<unknown[] | null> =>
    fetch(`${SUPA_URL}/rest/v1/rpc/${name}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: ANON,
        Authorization: `Bearer ${ANON}`,
      },
      body: JSON.stringify(body),
    })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);

  (window as any).__adtaria_blog_prefetch = {
    featured: rpc("get_featured_blog_posts", { p_limit: 1 }),
    posts: rpc("get_published_blog_posts", {
      p_limit: 24,
      p_offset: 0,
      p_search_term: null,
      p_tag_ids: null,
      p_exclude_featured: true,
    }),
    tags: rpc("get_blog_tags", {}),
  };

  // For repeat visitors: inject a preload hint for the cached featured image so
  // the browser starts downloading it before React even mounts.
  try {
    const raw = localStorage.getItem("adtaria_featured_post_v1");
    if (raw) {
      const { post, cachedAt } = JSON.parse(raw) as { post: { cover_image?: string }; cachedAt: number };
      if (Date.now() - cachedAt < 60 * 60 * 1000 && post?.cover_image) {
        const toRenderUrl = (url: string, w: number) => {
          try {
            const u = new URL(url);
            u.pathname = u.pathname.replace(
              "/storage/v1/object/public/",
              "/storage/v1/render/image/public/"
            );
            u.search = "";
            u.searchParams.set("width", String(w));
            u.searchParams.set("quality", "75");
            u.searchParams.set("format", "webp");
            return u.toString();
          } catch { return url; }
        };
        const link = document.createElement("link");
        link.rel = "preload";
        link.as = "image";
        link.href = toRenderUrl(post.cover_image, 1200);
        link.setAttribute(
          "imagesrcset",
          [640, 960, 1200, 1600].map((w) => `${toRenderUrl(post.cover_image!, w)} ${w}w`).join(", ")
        );
        link.setAttribute("imagesizes", "(min-width: 1024px) 50vw, 100vw");
        document.head.appendChild(link);
      }
    }
  } catch {
    // localStorage unavailable — silent fail
  }
}

// GA4 — marketing/public pages only (never on authenticated app routes)
// Deferred until idle to avoid competing with LCP resources.
// Initialization runs directly in the module bundle (no inline <script> element)
// so it complies with CSP script-src without needing 'unsafe-inline' or a nonce.
if (isHomePage || isMarketingPage || path === "/consulting") {
  const initGA4 = () => {
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    // Must be a regular function (not arrow) so the `arguments` object is available.
    // GA4 relies on dataLayer receiving the Arguments object, not a rest-param array.
    // eslint-disable-next-line prefer-rest-params
    w.gtag = function gtag() { w.dataLayer.push(arguments); };
    w.gtag("js", new Date());
    w.gtag("config", "G-56GP9QPKWJ");

    // Load the external GTM script — allowed by CSP: https://www.googletagmanager.com
    const gtagScript = document.createElement("script");
    gtagScript.async = true;
    gtagScript.src = "https://www.googletagmanager.com/gtag/js?id=G-56GP9QPKWJ";
    document.head.appendChild(gtagScript);
  };

  if ("requestIdleCallback" in window) {
    (window as any).requestIdleCallback(initGA4, { timeout: 5000 });
  } else {
    setTimeout(initGA4, 2000);
  }
}

if (path === "/consulting") {
  // Consulting has its own standalone entry
  Promise.all([
    import("./pages/Consulting"),
    import("react-router-dom")
  ]).then(([{ default: ConsultingPage }, { BrowserRouter }]) => {
    root.render(
      <StrictMode>
        <BrowserRouter>
          <ConsultingPage />
        </BrowserRouter>
      </StrictMode>
    );
  });
} else if (isHomePage) {
  // Home page — standalone marketing page
  import("./pages/App").then(({ default: MarketingPage }) => {
    root.render(
      <StrictMode>
        <MarketingPage />
      </StrictMode>
    );
  });
} else if (isMarketingPage) {
  // Lightweight marketing router — no Sentry, no product app providers
  import("./marketing-entry").then(({ default: MarketingEntry }) => {
    root.render(
      <StrictMode>
        <MarketingEntry />
      </StrictMode>
    );
  });
} else {
  // Full product app with Sentry, auth providers, etc.
  import("./app-entry").then(({ default: AppEntry }) => {
    root.render(
      <StrictMode>
        <AppEntry />
      </StrictMode>
    );
  });
}
